import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { crmService } from '../../services/crmService';
import { ColumnConfiguration } from '../../types/crm';

interface ColumnManagerProps {
    isOpen: boolean;
    onClose: () => void;
    tableName?: string;
    onColumnsUpdate?: (columns: ColumnConfiguration[]) => void;
}

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    justify-content: flex-end;
`;

const Drawer = styled.div`
    width: 400px;
    max-width: 90vw;
    height: 100vh;
    background: white;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
        margin: 0;
        font-size: 1.25rem;
        color: #111827;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6b7280;
    padding: 0.25rem;

    &:hover {
        color: #111827;
    }
`;

const Content = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
`;

const ColumnList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const ColumnItem = styled.div<{ $isDragging?: boolean; $visible: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: ${props => props.$visible ? '#f9fafb' : '#fef2f2'};
    border: 1px solid ${props => props.$visible ? '#e5e7eb' : '#fecaca'};
    border-radius: 6px;
    cursor: grab;
    opacity: ${props => props.$isDragging ? 0.5 : 1};
    transition: all 0.2s;

    &:hover {
        background: ${props => props.$visible ? '#f3f4f6' : '#fee2e2'};
    }
`;

const DragHandle = styled.span`
    color: #9ca3af;
    cursor: grab;
    font-size: 1.2rem;
`;

const ColumnInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const ColumnName = styled.input`
    width: 100%;
    border: 1px solid transparent;
    background: transparent;
    padding: 0.25rem 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: #111827;
    border-radius: 4px;

    &:hover {
        border-color: #d1d5db;
        background: white;
    }

    &:focus {
        outline: none;
        border-color: #2563eb;
        background: white;
    }
`;

const ColumnKey = styled.span`
    font-size: 0.75rem;
    color: #6b7280;
    display: block;
    margin-top: 0.125rem;
    padding-left: 0.5rem;
`;

const VisibilityToggle = styled.button<{ $visible: boolean }>`
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 0.25rem;
    color: ${props => props.$visible ? '#10b981' : '#ef4444'};

    &:hover {
        transform: scale(1.1);
    }
`;

const TypeBadge = styled.span<{ $type: string }>`
    font-size: 0.7rem;
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    text-transform: uppercase;
    font-weight: 600;
    background: ${props => {
        switch (props.$type) {
            case 'text': return '#dbeafe';
            case 'select': return '#dcfce7';
            case 'checkbox': return '#fef3c7';
            case 'textarea': return '#f3e8ff';
            default: return '#e5e7eb';
        }
    }};
    color: ${props => {
        switch (props.$type) {
            case 'text': return '#1d4ed8';
            case 'select': return '#166534';
            case 'checkbox': return '#92400e';
            case 'textarea': return '#7e22ce';
            default: return '#374151';
        }
    }};
`;

const Footer = styled.div`
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 1rem;
`;

const Button = styled.button<{ $primary?: boolean }>`
    flex: 1;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    ${props => props.$primary ? `
        background: #2563eb;
        color: white;
        border: none;

        &:hover {
            background: #1d4ed8;
        }
    ` : `
        background: white;
        color: #374151;
        border: 1px solid #d1d5db;

        &:hover {
            background: #f9fafb;
        }
    `}

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const AddColumnSection = styled.div`
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
`;

const AddColumnForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const FormRow = styled.div`
    display: flex;
    gap: 0.5rem;

    input, select {
        flex: 1;
        padding: 0.5rem;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        font-size: 0.9rem;

        &:focus {
            outline: none;
            border-color: #2563eb;
        }
    }
`;

const AddButton = styled.button`
    background: #10b981;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;

    &:hover {
        background: #059669;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ColumnManager: React.FC<ColumnManagerProps> = ({
    isOpen,
    onClose,
    tableName = 'tracks',
    onColumnsUpdate
}) => {
    const [columns, setColumns] = useState<ColumnConfiguration[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pendingChanges, setPendingChanges] = useState<Map<number, Partial<ColumnConfiguration>>>(new Map());
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    // New column form state
    const [newColumnKey, setNewColumnKey] = useState('');
    const [newColumnName, setNewColumnName] = useState('');
    const [newColumnType, setNewColumnType] = useState<'text' | 'select' | 'checkbox' | 'textarea'>('text');

    const fetchColumns = useCallback(async () => {
        setLoading(true);
        try {
            const response = await crmService.getColumnSchema(tableName);
            setColumns(response.columns);
        } catch (error) {
            console.error('Failed to fetch columns:', error);
        } finally {
            setLoading(false);
        }
    }, [tableName]);

    useEffect(() => {
        if (isOpen) {
            fetchColumns();
        }
    }, [isOpen, fetchColumns]);

    const handleNameChange = (columnId: number, newName: string) => {
        setColumns(prev => prev.map(col =>
            col.id === columnId ? { ...col, display_name: newName } : col
        ));
        setPendingChanges(prev => {
            const updated = new Map(prev);
            const existing = updated.get(columnId) || {};
            updated.set(columnId, { ...existing, display_name: newName });
            return updated;
        });
    };

    const handleVisibilityToggle = (columnId: number) => {
        setColumns(prev => prev.map(col =>
            col.id === columnId ? { ...col, visible: !col.visible } : col
        ));
        const column = columns.find(c => c.id === columnId);
        if (column) {
            setPendingChanges(prev => {
                const updated = new Map(prev);
                const existing = updated.get(columnId) || {};
                updated.set(columnId, { ...existing, visible: !column.visible });
                return updated;
            });
        }
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newColumns = [...columns];
        const draggedColumn = newColumns[draggedIndex];
        newColumns.splice(draggedIndex, 1);
        newColumns.splice(index, 0, draggedColumn);

        setColumns(newColumns);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Save pending name/visibility changes
            for (const [columnId, changes] of pendingChanges) {
                await crmService.updateColumn(columnId, changes);
            }

            // Save new order
            const columnIds = columns.map(c => c.id);
            const response = await crmService.reorderColumns(columnIds, tableName);

            setColumns(response.columns);
            setPendingChanges(new Map());

            if (onColumnsUpdate) {
                onColumnsUpdate(response.columns);
            }
        } catch (error) {
            console.error('Failed to save changes:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleAddColumn = async () => {
        if (!newColumnKey || !newColumnName) return;

        try {
            const newColumn = await crmService.createColumn({
                table_name: tableName,
                column_key: newColumnKey,
                display_name: newColumnName,
                column_type: newColumnType,
                visible: true,
                editable: true
            });

            setColumns(prev => [...prev, newColumn]);
            setNewColumnKey('');
            setNewColumnName('');
            setNewColumnType('text');

            if (onColumnsUpdate) {
                onColumnsUpdate([...columns, newColumn]);
            }
        } catch (error) {
            console.error('Failed to add column:', error);
        }
    };

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <Drawer onClick={e => e.stopPropagation()}>
                <Header>
                    <h2>Manage Columns</h2>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </Header>

                <Content>
                    {loading ? (
                        <div>Loading columns...</div>
                    ) : (
                        <>
                            <ColumnList>
                                {columns.map((column, index) => (
                                    <ColumnItem
                                        key={column.id}
                                        $isDragging={draggedIndex === index}
                                        $visible={column.visible}
                                        draggable
                                        onDragStart={() => handleDragStart(index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <DragHandle>⋮⋮</DragHandle>
                                        <ColumnInfo>
                                            <ColumnName
                                                value={column.display_name}
                                                onChange={(e) => handleNameChange(column.id, e.target.value)}
                                            />
                                            <ColumnKey>{column.column_key}</ColumnKey>
                                        </ColumnInfo>
                                        <TypeBadge $type={column.column_type}>
                                            {column.column_type}
                                        </TypeBadge>
                                        <VisibilityToggle
                                            $visible={column.visible}
                                            onClick={() => handleVisibilityToggle(column.id)}
                                            title={column.visible ? 'Hide column' : 'Show column'}
                                        >
                                            {column.visible ? '👁' : '👁‍🗨'}
                                        </VisibilityToggle>
                                    </ColumnItem>
                                ))}
                            </ColumnList>

                            <AddColumnSection>
                                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#374151' }}>
                                    Add New Column
                                </h4>
                                <AddColumnForm>
                                    <FormRow>
                                        <input
                                            type="text"
                                            placeholder="Column key (e.g., bpm)"
                                            value={newColumnKey}
                                            onChange={(e) => setNewColumnKey(e.target.value)}
                                        />
                                    </FormRow>
                                    <FormRow>
                                        <input
                                            type="text"
                                            placeholder="Display name (e.g., BPM)"
                                            value={newColumnName}
                                            onChange={(e) => setNewColumnName(e.target.value)}
                                        />
                                        <select
                                            value={newColumnType}
                                            onChange={(e) => setNewColumnType(e.target.value as typeof newColumnType)}
                                        >
                                            <option value="text">Text</option>
                                            <option value="select">Select</option>
                                            <option value="checkbox">Checkbox</option>
                                            <option value="textarea">Textarea</option>
                                        </select>
                                    </FormRow>
                                    <AddButton
                                        onClick={handleAddColumn}
                                        disabled={!newColumnKey || !newColumnName}
                                    >
                                        + Add Column
                                    </AddButton>
                                </AddColumnForm>
                            </AddColumnSection>
                        </>
                    )}
                </Content>

                <Footer>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        $primary
                        onClick={handleSave}
                        disabled={saving || (pendingChanges.size === 0 && draggedIndex === null)}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Footer>
            </Drawer>
        </Overlay>
    );
};

export default ColumnManager;
